/**
 * Active org/store context.
 *
 * Every service function in this package accepts an `ActiveContext` so the
 * caller MUST be explicit about which tenant they're operating on. The web
 * shell stores this in a React context and passes it into every call -- there
 * is no implicit "current org" inside `@pc/db`.
 *
 * `storeId` is nullable so org-mode call sites (the "Enter organization"
 * shell at `/choose-context`) can pass `{ orgId, storeId: null }` to the
 * subset of services that natively aggregate across stores (today: the
 * calendar agenda and the new `*_org_list_*` / `rpc_org_*` aggregate RPCs).
 * Store-scoped services keep calling `requireContext` which still throws
 * loudly on a null store -- the type widening is purely additive.
 */
export interface ActiveContext {
  orgId: string;
  storeId: string | null;
}

/**
 * Narrower variant returned by per-store module `useActiveCtx()` adapters
 * (clients, orders, wholesalers, vendors, shipping, fulfillment,
 * contracts, shop). Those modules throw `MissingActiveContextError` when
 * `storeId` is null, so they safely declare a non-nullable shape; their
 * consumers can read `ctx.storeId` as `string` without a guard.
 *
 * The calendar module's adapter is the exception -- it returns the
 * broader `ActiveContext` because the module renders under both the
 * store shell and the org shell.
 */
export interface StoreActiveContext {
  orgId: string;
  storeId: string;
}

/**
 * Strict variant: requires BOTH org and store. Used by every per-store
 * service (clients.listClients, orders.listOrders, ...). Narrows the
 * return type so the caller can destructure `storeId` as `string`.
 */
export function requireContext(
  ctx: ActiveContext | null | undefined,
): { orgId: string; storeId: string } {
  if (!ctx?.orgId || !ctx?.storeId) {
    throw new Error('[@pc/db] Missing active org/store context. Pass { orgId, storeId }.');
  }
  return { orgId: ctx.orgId, storeId: ctx.storeId };
}

/**
 * Loose variant: requires only `orgId`. Used by services that explicitly
 * handle a null `storeId` (today: calendar.listAppointments, the
 * `rpc_org_*` aggregate wrappers). Returns the same shape as ActiveContext
 * so call sites can branch on `if (storeId) { ... }`.
 */
export function requireOrgContext(
  ctx: ActiveContext | null | undefined,
): { orgId: string; storeId: string | null } {
  if (!ctx?.orgId) {
    throw new Error('[@pc/db] Missing active org context. Pass { orgId }.');
  }
  return { orgId: ctx.orgId, storeId: ctx.storeId ?? null };
}
